import { useEffect, useState } from "react";
import { finAPI, fmtINR, fmtDate, STATUS_STYLE } from "./finApi";
import { Plus, X, Wallet, Filter, TrendingDown, TrendingUp } from "lucide-react";

function OpenDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ float_amount: "", drawer_code: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await finAPI.openShift(body);
      onCreated(); onClose();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fin-open-shift-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Open Cash Shift</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Opening Float (₹) *</label>
            <input value={form.float_amount} onChange={e => setForm({...form, float_amount: e.target.value})}
              required pattern="^\d+(\.\d{1,2})?$" data-testid="fin-shift-float"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Drawer Code</label>
            <input value={form.drawer_code} onChange={e => setForm({...form, drawer_code: e.target.value})}
              data-testid="fin-shift-drawer"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              rows={2} data-testid="fin-shift-notes"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </div>
          {err && <div className="text-rose-700 text-[12px] bg-rose-50 px-3 py-2 rounded-lg">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] font-medium text-slate-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="fin-shift-open-submit"
              className="px-5 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium disabled:opacity-60">
              {busy ? "Opening…" : "Open Shift"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShiftDrawer({ shift, onClose, onChange }) {
  const [txn, setTxn] = useState({ kind: "collection", amount: "", reference_no: "", notes: "" });
  const [close, setClose] = useState({ counted_amount: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!shift) return null;

  const totals = (() => {
    const opened = Number(shift.float_amount || 0);
    let coll = 0, dep = 0, pay = 0, adj = 0;
    (shift.txns || []).forEach(t => {
      const v = Number(t.amount || 0);
      if (t.kind === "collection") coll += v;
      else if (t.kind === "deposit") dep += v;
      else if (t.kind === "payout") pay += v;
      else if (t.kind === "adjustment") adj += v;
    });
    return { opened, coll, dep, pay, adj, expected: opened + coll - dep - pay + adj };
  })();

  const addTxn = async () => {
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(Object.entries(txn).filter(([, v]) => v !== ""));
      await finAPI.addShiftTxn(shift.id, body);
      setTxn({ kind: "collection", amount: "", reference_no: "", notes: "" });
      onChange();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doClose = async () => {
    if (!close.counted_amount) { setErr("Counted amount required"); return; }
    setBusy(true); setErr(null);
    try {
      await finAPI.closeShift(shift.id, close);
      onChange(); onClose();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end" data-testid="fin-shift-drawer">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-[16px] font-semibold text-hocp-navy">{shift.code}</h3>
            <div className="text-[12px] text-slate-500">Opened {fmtDate(shift.opened_at)} · {shift.operator_email}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <div className="p-6 space-y-6">
          {/* Totals */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { l: "Opening Float",    v: fmtINR(totals.opened),  cls: "bg-slate-50" },
              { l: "Collections",       v: fmtINR(totals.coll),    cls: "bg-emerald-50 text-emerald-800" },
              { l: "Deposits",          v: fmtINR(totals.dep),     cls: "bg-sky-50 text-sky-800" },
              { l: "Payouts",           v: fmtINR(totals.pay),     cls: "bg-rose-50 text-rose-800" },
              { l: "Adjustments",       v: fmtINR(totals.adj),     cls: "bg-amber-50 text-amber-800" },
              { l: "Expected in Drawer",v: fmtINR(totals.expected),cls: "bg-hocp-navy text-white col-span-2" },
            ].map(t => (
              <div key={t.l} className={`p-3 rounded-xl ${t.cls}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-80">{t.l}</div>
                <div className="font-display text-[18px] font-semibold tabular-nums mt-1">{t.v}</div>
              </div>
            ))}
          </div>

          {shift.status === "open" && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Add Transaction</div>
              <div className="grid grid-cols-2 gap-3">
                <select value={txn.kind} onChange={e => setTxn({...txn, kind: e.target.value})}
                  data-testid="fin-shift-txn-kind"
                  className="px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                  <option value="collection">Collection (+)</option>
                  <option value="deposit">Deposit (−)</option>
                  <option value="payout">Payout (−)</option>
                  <option value="adjustment">Adjustment (+)</option>
                </select>
                <input value={txn.amount} onChange={e => setTxn({...txn, amount: e.target.value})}
                  pattern="^\d+(\.\d{1,2})?$" placeholder="Amount (₹)" data-testid="fin-shift-txn-amount"
                  className="px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
              </div>
              <input value={txn.reference_no} onChange={e => setTxn({...txn, reference_no: e.target.value})}
                placeholder="Reference" data-testid="fin-shift-txn-ref"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
              <button onClick={addTxn} disabled={busy || !txn.amount} data-testid="fin-shift-txn-add"
                className="w-full px-4 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium disabled:opacity-50">
                {busy ? "Adding…" : "Add"}
              </button>
            </div>
          )}

          {(shift.txns || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">Ledger ({shift.txns.length})</div>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-[12px]">
                  <tbody className="divide-y divide-slate-100">
                    {shift.txns.map((t, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            t.kind === "collection" ? "bg-emerald-100 text-emerald-700" :
                            t.kind === "deposit" ? "bg-sky-100 text-sky-700" :
                            t.kind === "payout" ? "bg-rose-100 text-rose-700" :
                            "bg-amber-100 text-amber-700"}`}>{t.kind}</span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtINR(t.amount)}</td>
                        <td className="px-3 py-2 text-slate-500">{t.reference_no || "—"}</td>
                        <td className="px-3 py-2 text-slate-400 text-[11px]">{fmtDate(t.at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {shift.status === "open" && (
            <div className="border border-rose-200 bg-rose-50 rounded-xl p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-rose-700 font-medium">Close Shift</div>
              <input value={close.counted_amount} onChange={e => setClose({...close, counted_amount: e.target.value})}
                pattern="^\d+(\.\d{1,2})?$" placeholder="Physically counted amount (₹)"
                data-testid="fin-shift-close-counted"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
              <textarea value={close.notes} onChange={e => setClose({...close, notes: e.target.value})}
                rows={2} placeholder="Closing notes"
                data-testid="fin-shift-close-notes"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
              <button onClick={doClose} disabled={busy || !close.counted_amount} data-testid="fin-shift-close-submit"
                className="w-full px-4 py-2 rounded-lg bg-rose-600 text-white text-[13px] font-medium disabled:opacity-50">
                Close Shift & Compute Variance
              </button>
            </div>
          )}

          {shift.status === "closed" && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Close Summary</div>
              <div className="flex justify-between text-[13px]"><span>Expected</span><span className="font-mono tabular-nums">{fmtINR(shift.expected_amount)}</span></div>
              <div className="flex justify-between text-[13px]"><span>Counted</span><span className="font-mono tabular-nums">{fmtINR(shift.counted_amount)}</span></div>
              <div className={`flex justify-between text-[14px] font-semibold border-t pt-2 mt-2 ${
                Number(shift.variance) === 0 ? "text-emerald-700" :
                Number(shift.variance) > 0 ? "text-sky-700" : "text-rose-700"}`}>
                <span>Variance</span>
                <span className="font-mono tabular-nums flex items-center gap-1">
                  {Number(shift.variance) === 0 ? null : Number(shift.variance) > 0 ? <TrendingUp className="w-3.5 h-3.5"/> : <TrendingDown className="w-3.5 h-3.5"/>}
                  {fmtINR(shift.variance)}
                </span>
              </div>
            </div>
          )}

          {err && <div className="text-rose-700 text-[12px] bg-rose-50 px-3 py-2 rounded-lg">{err}</div>}
        </div>
      </div>
    </div>
  );
}

export default function FinanceCash() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const rows = await finAPI.shifts(status ? { status } : {}).catch(() => []);
    setItems(rows || []);
    if (selected) {
      const fresh = (rows || []).find(s => s.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="space-y-4" data-testid="fin-cash-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12px] text-slate-500"><Filter className="w-4 h-4"/> Filter:</div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="fin-cash-filter"
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
          <option value="">All shifts</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
        <div className="flex-1"/>
        <button onClick={() => setOpenDialog(true)} data-testid="fin-open-shift-btn"
          className="px-4 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium inline-flex items-center gap-2 hover:brightness-110">
          <Plus className="w-4 h-4"/> Open Shift
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.length === 0 ? (
          <div className="col-span-full p-12 text-center bg-white border border-slate-200 rounded-2xl" data-testid="fin-cash-empty">
            <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-[13px] text-slate-500">No shifts yet.</div>
          </div>
        ) : items.map(s => {
          const st = STATUS_STYLE[s.status] || { chip: "bg-slate-100 text-slate-700", label: s.status };
          return (
            <button key={s.id} onClick={() => setSelected(s)} data-testid={`fin-shift-card-${s.code}`}
              className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:border-hocp-navy hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[13px] text-hocp-navy font-semibold">{s.code}</div>
                <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${st.chip}`}>{st.label}</span>
              </div>
              <div className="text-[12px] text-slate-500">{s.operator_email}</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
                <div>
                  <div className="text-slate-500">Float</div>
                  <div className="font-mono font-medium">{fmtINR(s.float_amount)}</div>
                </div>
                <div>
                  <div className="text-slate-500">{s.status === "closed" ? "Variance" : "Txns"}</div>
                  <div className={`font-mono font-medium ${s.status === "closed" ? (Number(s.variance) === 0 ? "text-emerald-700" : "text-rose-700") : ""}`}>
                    {s.status === "closed" ? fmtINR(s.variance) : (s.txns?.length || 0)}
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-slate-400">Opened {fmtDate(s.opened_at)}</div>
            </button>
          );
        })}
      </div>

      <OpenDialog open={openDialog} onClose={() => setOpenDialog(false)} onCreated={load}/>
      <ShiftDrawer shift={selected} onClose={() => setSelected(null)} onChange={load}/>
    </div>
  );
}
